import { Box, Skeleton, Stack } from "@mui/material";
import { isTempId, taskColumnOptionSelectByQuery } from "@common-reducers/DBServiceThunks";
import { useAppSelector } from "@common-reducers/hooks/store.hook";

import { memo, useEffect, useMemo, useRef, useState } from "react";
import { CellProps } from "../cell-interface";
import classes from "./option-picker.module.scss";
import ComplexityBadge from "./complexity-badge";
import Lottie from "@/common/components/lazy-lottie/lazy-lottie";
import Checkmark from '@common-assets/lottie/checkmark.json';

function OptionPickerCell({ taskId, cellValue, columnId, boardId, togglePicker, sx, mockValue, readOnly }: CellProps) {
  const pickerAnchorId = `options_${taskId}_${columnId}`;
  const [showAnimation, setShowAnimation] = useState(false);
  const previousColumnId = usePrevious(columnId);
  const firstRender = useRef(true); // useRef to track the first render

  const columnOptions = useAppSelector((state) => taskColumnOptionSelectByQuery(state.DBTaskColumnOptionReducer, {
    boardId,
    columnId,
    $or: [{ deleted: { $exists: false } }, { deleted: false }],
  }))
  const option = useMemo(() => columnOptions.find((opt) => opt._id && !isTempId(opt._id) && opt._id === cellValue), [columnOptions, cellValue]) ?? mockValue

  const defaultOption = useMemo(() => columnOptions.find((opt) => opt.isDefaultLabel), [columnOptions, cellValue])
  // const columnType = useAppSelector((state) => taskColumnSelectOneFieldByQuery(state.DBTaskColumnReducer, { _id: columnId }, 'type'))

  // const isComplexity = (columnType === 'complexity-picker') ? true : false
  const isComplexity = option?.complexity ? option?.complexity : mockValue?.complexity
  const optionLabel = option?.label ? option?.label : mockValue?.label ?? null;


  const styling: any = {
    height: 25,
    borderRadius: '5px',
    flex: 'auto',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...sx
  }
  const bgColor = option?.bgcolor ? option?.bgcolor : mockValue?.bgcolor ?? '#c4c4c4f5'

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false; 
      return; 
    }
    if (optionLabel === "Done" && previousColumnId === columnId) {
      setShowAnimation(true);
      const timeoutId = setTimeout(() => setShowAnimation(false), 2000); 
      return () => clearTimeout(timeoutId);
    }

  }, [optionLabel,  columnId,  previousColumnId]);



  
  return (
    <Stack
      direction="row"
      sx={{
        width: 1,
        position: "relative",
        padding: sx ? '0px' : "0 5px",
        overflow: "hidden"
      }}
      id={pickerAnchorId}
      onClick={(event) => event.stopPropagation()}
      key={pickerAnchorId}
    >
      {isTempId(columnId ?? null) &&
        <Skeleton sx={{ background: '#c4c4c4f5' }} variant="rectangular" />
      }
      {!isTempId(columnId ?? null) && !isComplexity &&
        <button
          aria-haspopup="true"
          onClick={() => togglePicker({
            anchorId: pickerAnchorId,
            pickerType: 'option-picker'
          })}
          className={classes.pickerButton}
          style={{
            backgroundColor: bgColor,
            textTransform: "none",
            boxShadow: "none",
            color: "var(--task-option-text)",
            fontSize: 12,
            fontWeight: "600",
            fontFamily: "'Inter', 'Assistant', sans-serif",
            ...styling
          }}
          disabled={readOnly}
        >
          {showAnimation  &&
            <Box sx={{ position: "absolute", top: 0, right: 0, left: "0%", overFlow: "hidden" }}>
              <Lottie autoPlay={true} loop={false} animationData={Checkmark} style={{ width: '100%', height: '150px' }} />
            </Box>
          }
          {optionLabel ?? defaultOption?.label}
        </button>
      }
      {!isTempId(columnId ?? null) && isComplexity &&
        <button
          aria-haspopup="true"
          onClick={() => togglePicker({
            anchorId: pickerAnchorId,
            pickerType: 'option-picker'
          })}
          className={classes.pickerButton}
          style={{
            backgroundColor: bgColor,
            textTransform: "none",
            boxShadow: "none",
            color: "var(--task-option-text)",
            fontSize: 12,
            fontWeight: "600",
            fontFamily: "'Inter', 'Assistant', sans-serif",
            ...styling
          }}
          disabled={readOnly}
        >
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 6,
            paddingRight: '4px',
            paddingLeft: '6px',
          }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{optionLabel ?? defaultOption?.label}</div>
            <ComplexityBadge value={option?.complexity ? option?.complexity : mockValue?.complexity} />
          </div>
        </button>
      }
    </Stack>
  );
}

export default memo(OptionPickerCell);

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  
  useEffect(() => {
    ref.current = value;
  }, [value]);
  
  return ref.current;
}